// Updates right-edge Team / Media panel (Capacity Scenario rail pattern).
// Team ~310px; Media ~half the page with a large read-only two-column photo
// grid and shared Documents. Upload/reorder stay on the drawer Media tab.
// Archived (frozen/history) weeks only show captured evidence.
import { useEffect, useMemo, useState } from "react";
import { Users, Image as ImageIcon } from "lucide-react";
import { T } from "../../../app/tokens.js";
import { SidePanelRail } from "../../../components/SidePanelRail.jsx";
import { getProjectMedia, onProjectMediaChanged } from "../../../data/projectMediaClient.js";
import { MediaDrawerViewer } from "../media/MediaDrawerViewer.jsx";
import { safeMediaUrl } from "../media/MediaStrip.jsx";
import { orderMedia, resolveCover, mediaLabels } from "../media/mediaDisplay.js";
import { ProjectDocuments } from "../documents/ProjectDocuments.jsx";

const PANEL_CSS = `
.wk-sidehost { display: flex; gap: 12px; align-items: flex-start; min-width: 0; }
.wk-sidehost > [data-testid="updates-main"] { flex: 1 1 0; min-width: 0; }
.wk-sidestack { display: flex; gap: 12px; align-items: flex-start; flex: 0 0 auto; min-width: 0; box-sizing: border-box; position: sticky; top: calc(var(--updates-sticky-top, 56px) + var(--wk-head-h, 0px) + 12px); }
.wk-sidestack[data-mode="media"] { flex: 0 0 50%; max-width: 50%; }
.wk-sidepanel { box-sizing: border-box; background: ${T.card}; border: 1px solid ${T.border}; border-radius: 12px; overflow: hidden; min-width: 0; }
.wk-sidepanel[data-mode="team"] { flex: 0 0 310px; width: 310px; }
.wk-sidepanel[data-mode="media"] { flex: 1 1 0; }
.wk-sidestack > nav { flex: 0 0 44px; }
.wk-sidepanel-body { max-height: calc(var(--wk-viewport-h, 100dvh) - var(--updates-sticky-top, 56px) - var(--wk-head-h, 0px) - 90px); overflow: auto; }
.wk-photos { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
@media (max-width: 1100px) {
  .wk-sidehost { flex-wrap: wrap; }
  .wk-sidestack, .wk-sidestack[data-mode="media"] { position: static; flex: 1 1 100%; max-width: 100%; width: 100%; }
  .wk-sidestack { order: -1; }
  .wk-sidestack[data-mode="collapsed"] { justify-content: flex-end; }
  .wk-sidepanel[data-mode] { flex: 1 1 0; width: auto; }
  .wk-sidepanel-body { max-height: none; }
}
@media (max-width: 520px) { .wk-photos { grid-template-columns: minmax(0, 1fr); } }
`;

function useUpdatesPhotos(projectId, captured, enabled) {
  const [state, setState] = useState({ status: "loading", items: [], coverMediaId: null, effectiveCover: null });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    if (captured !== undefined) { setState({ status: "ok", items: Array.isArray(captured) ? captured : [], coverMediaId: null, effectiveCover: null, captured: true }); return undefined; }
    let active = true;
    const load = (quiet) => {
      if (!quiet) setState((s) => ({ ...s, status: "loading" }));
      Promise.resolve().then(() => getProjectMedia(projectId))
        .then((r) => { if (active) setState({ status: "ok", items: r.items, coverMediaId: r.coverMediaId, effectiveCover: r.effectiveCover }); })
        .catch((err) => { if (active) setState({ status: "error", items: [], error: err?.message || "Request failed." }); });
    };
    load(false);
    const off = onProjectMediaChanged(projectId, () => load(true));
    return () => { active = false; off(); };
  }, [projectId, captured, enabled, retry]);
  return [state, () => setRetry((n) => n + 1)];
}

function PhotoGrid({ state, retry, cover, images, archivedNoMedia }) {
  const [active, setActive] = useState(null);
  if (archivedNoMedia) return <p data-testid="updates-photos-unavailable" style={{ color: T.mid, fontSize: T.fsXs, fontStyle: "italic" }}>Media references were not captured with this archive.</p>;
  if (state.status === "loading") return <div className="wk-photos" aria-busy="true" data-testid="updates-photos-loading">{[0, 1, 2, 3].map((i) => <div key={i} style={{ aspectRatio: "4 / 3", borderRadius: 8, background: T.lightGray }} />)}</div>;
  if (state.status === "error") return <div role="alert" style={{ color: T.red, fontSize: T.fsXs }}>Couldn't load photos: {state.error} <button type="button" data-testid="button-updates-photos-retry" onClick={retry} style={{ border: `1px solid ${T.border}`, background: T.card, borderRadius: 6, padding: "2px 9px", cursor: "pointer" }}>Retry</button></div>;
  return (
    <>
      {images.length === 0
        ? <p data-testid="updates-photos-empty" style={{ color: T.mid, fontSize: T.fsXs }}>{state.captured ? "No photos were captured with this archive." : "No photos yet. Add photos from the project drawer's Media tab."}</p>
        : <div className="wk-photos" data-testid="updates-photos">
          {images.map((item) => {
            const src = safeMediaUrl(item.thumbnailUrl || item.url);
            const label = mediaLabels(item).primary;
            const isCover = !state.captured && cover.id != null && String(cover.id) === String(item.id);
            return (
              <button key={item.id} type="button" data-testid={`updates-photo-${item.id}`} aria-label={`View ${label} full screen`} onClick={() => setActive(item.id)}
                style={{ position: "relative", aspectRatio: "4 / 3", borderRadius: 8, border: 0, padding: 0, overflow: "hidden", background: T.lightGray, cursor: "zoom-in" }}>
                {src && <img src={src} alt="" loading="lazy" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} />}
                {isCover && <span style={{ position: "absolute", left: 8, top: 8, background: T.dark, color: T.white, fontSize: T.fsXxs, fontWeight: T.fwBold, borderRadius: 5, padding: "2px 6px" }}>Cover</span>}
              </button>
            );
          })}
        </div>}
      {!state.captured && <p style={{ fontSize: T.fsXxs, color: T.mid, margin: "8px 0 0" }}>Click a photo for full-screen view with arrows. Upload and reorder stay on the drawer's Media tab.</p>}
      <MediaDrawerViewer items={images} activeId={active} onIndex={setActive} onClose={() => setActive(null)} host={null} />
    </>
  );
}

function MediaSwitch({ tab, onChange, photos, documents }) {
  const seg = (on) => ({ padding: "5px 12px", fontSize: T.fsXs, fontWeight: T.fwSemibold, border: 0, borderRight: `1px solid ${T.borderDark}`, cursor: "pointer", fontFamily: "inherit", background: on ? T.blueSoft : T.card, color: on ? T.blue : T.dark });
  return (
    <span role="group" aria-label="Media view" style={{ display: "inline-flex", border: `1px solid ${T.borderDark}`, borderRadius: 8, overflow: "hidden" }}>
      <button type="button" data-testid="updates-media-switch-photos" aria-pressed={tab === "photos"} onClick={() => onChange("photos")} style={seg(tab === "photos")}>Photos{photos != null ? ` ${photos}` : ""}</button>
      <button type="button" data-testid="updates-media-switch-documents" aria-pressed={tab === "documents"} onClick={() => onChange("documents")} style={{ ...seg(tab === "documents"), borderRight: 0 }}>Documents{documents != null ? ` ${documents}` : ""}</button>
    </span>
  );
}

/**
 * pref/onPref: { mode, tab } from useUpdatesPanelPreference.
 * archived: frozen/history week; docs is null there (no live documents).
 */
export { PANEL_CSS as UPDATES_PANEL_CSS };

export function UpdatesSidePanel({ projectId, pref, onPref, team, teamSubtitle = null, archived, capturedMedia, docs, docSelected, onDocSelect, docsReadOnly, onDocsDraft }) {
  const mode = pref.mode;
  const items = [{ key: "team", label: "Team", icon: <Users size={14} aria-hidden="true" /> }, { key: "media", label: "Media", icon: <ImageIcon size={14} aria-hidden="true" /> }];
  // One media read shared by Photos and Documents (retained PDFs). Archived
  // weeks use captured references only and never read live media.
  const archivedNoMedia = archived && !Array.isArray(capturedMedia);
  const [photoState, retryPhotos] = useUpdatesPhotos(projectId, archived ? capturedMedia : undefined, mode === "media" && !archivedNoMedia);
  const cover = useMemo(() => resolveCover(photoState), [photoState]);
  const ordered = useMemo(() => (photoState.captured ? photoState.items : orderMedia(photoState.items, cover.id)), [photoState, cover.id]);
  const images = useMemo(() => ordered.filter((i) => i.type === "image"), [ordered]);
  const pdfs = useMemo(() => (archived ? [] : ordered.filter((i) => i.type === "pdf")), [ordered, archived]);
  const mediaOk = photoState.status === "ok" && !archivedNoMedia;
  const photoCount = mediaOk ? images.length : null;
  const docCount = !archived && docs && docs.status === "ok" && mediaOk ? docs.items.length + pdfs.length : null;
  return (
    <div className="wk-sidestack" data-testid="updates-side-panel" data-mode={mode}>
      <style>{PANEL_CSS}</style>
      {/* The aside stays mounted so an unsent Documents draft survives
          Team/Media/Photos switches and collapse; hidden parts are display:none. */}
      <aside className="wk-sidepanel" data-mode={mode} data-testid={`updates-panel-${mode}`} aria-label={mode === "team" ? "Team" : "Media"}
        hidden={mode === "collapsed" || undefined} style={mode === "collapsed" ? { display: "none" } : undefined}>
        {mode === "team" && (
          <div data-testid="updates-media-column">
            {teamSubtitle}
            {team}
          </div>
        )}
        <div hidden={mode !== "media" || undefined} style={mode !== "media" ? { display: "none" } : undefined}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: `1px solid ${T.border}`, flexWrap: "wrap" }}>
            <b style={{ fontSize: T.fsMd }}>Media</b>
            <MediaSwitch tab={pref.tab} onChange={(tab) => onPref({ tab })} photos={photoCount} documents={docCount} />
          </div>
          <div className="wk-sidepanel-body" style={{ padding: "12px 16px" }}>
            {mode === "media" && pref.tab === "photos" && <PhotoGrid state={photoState} retry={retryPhotos} cover={cover} images={images} archivedNoMedia={archivedNoMedia} />}
            {archived || !docs
              ? (mode === "media" && pref.tab === "documents" && <p data-testid="updates-documents-unavailable" style={{ color: T.mid, fontSize: T.fsXs, fontStyle: "italic" }}>Documents were not captured with this archive.</p>)
              : <div hidden={pref.tab !== "documents" || undefined} style={pref.tab !== "documents" ? { display: "none" } : undefined}>
                  <ProjectDocuments key={projectId} docs={docs} pdfItems={mediaOk ? pdfs : []} readOnly={docsReadOnly} selectedId={docSelected} onSelect={onDocSelect} onDraftState={onDocsDraft} testId="updates-documents" />
                </div>}
          </div>
        </div>
      </aside>
      <SidePanelRail items={items} active={mode === "collapsed" ? null : mode} testId="updates-rail" ariaLabel="Updates side panel"
        onSelect={(key) => onPref({ mode: key })} onCollapse={() => onPref({ mode: "collapsed" })} />
    </div>
  );
}
